import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Plus, X, Check, Send } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";

const PRODUCTS = ["Registration fee", "Stamp Duty", "Clerical Fees", "Cleaner charges", "Plumber Charges", "Decorator Charges"];
const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);
const BTN = "inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all active:scale-[0.98]";
const INPUT = "w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const fmt = (s) => { try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); } catch { return s; } };

export default function ContractorInvoice() {
  const { clients, selectedClientId } = usePortal();
  const client = clients.find((c) => c.id === selectedClientId);
  const [properties, setProperties] = useState([]);
  const [propertyName, setPropertyName] = useState("");
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [mine, setMine] = useState([]);

  const loadMine = useCallback(() => { if (selectedClientId) api.get(`/contractor-invoices?client_id=${selectedClientId}`).then((r) => setMine(r.data)).catch(() => {}); else setMine([]); }, [selectedClientId]);
  useEffect(() => {
    setPropertyName(""); setLines([]);
    if (selectedClientId) api.get(`/properties?client_id=${selectedClientId}`).then((r) => setProperties(r.data)).catch(() => setProperties([]));
    else setProperties([]);
    loadMine();
  }, [selectedClientId, loadMine]);

  const toggleProduct = (p) => setLines((ls) => (ls.find((l) => l.product === p) ? ls.filter((l) => l.product !== p) : [...ls, { product: p, amount_inr: "" }]));
  const setAmount = (p, v) => setLines((ls) => ls.map((l) => (l.product === p ? { ...l, amount_inr: v } : l)));
  const total = lines.reduce((s, l) => s + (Number(l.amount_inr) || 0), 0);

  const submit = async () => {
    if (!propertyName) return toast.error("Select a property");
    if (lines.length === 0) return toast.error("Select at least one product");
    if (lines.some((l) => !(Number(l.amount_inr) > 0))) return toast.error("Enter an amount for each line");
    setSubmitting(true);
    try {
      await api.post("/contractor-invoices", { client_id: selectedClientId, property_name: propertyName, lines: lines.map((l) => ({ product: l.product, amount_inr: Number(l.amount_inr) })) });
      toast.success("Invoice submitted and emailed to the administrator");
      setPropertyName(""); setLines([]); loadMine();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl" data-testid="contractor-invoice-page">
      <h1 className="text-3xl font-semibold tracking-tight font-display text-slate-900 flex items-center gap-2">
        <FileText className="h-7 w-7 text-emerald-600" /> Raise Invoice
      </h1>
      <p className="mt-2 text-sm text-slate-500">Raise a contractor invoice to VA Property Care for the selected client. The administrator is emailed a copy.</p>

      {!selectedClientId ? (
        <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-5 text-sm" data-testid="contractor-select-client-message">
          Please select a client from the <strong>Viewing client</strong> dropdown at the top-right first.
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">New invoice</h2>
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-600">Customer</label>
                  <input value="VA Property Care" disabled data-testid="contractor-customer" className={`${INPUT} mt-1.5 bg-slate-50 text-slate-500`} />
                </div>
                <div>
                  <label className="text-sm text-slate-600">Client</label>
                  <input value={client?.name || ""} disabled data-testid="contractor-client" className={`${INPUT} mt-1.5 bg-slate-50 text-slate-500`} />
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-600">Property</label>
                <select value={propertyName} onChange={(e) => setPropertyName(e.target.value)} data-testid="contractor-property-select" className={`${INPUT} mt-1.5`}>
                  <option value="">{properties.length ? "Select a property…" : "No properties for this client"}</option>
                  {properties.map((p) => <option key={p.id} value={p.address}>{p.address}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600">Products</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {PRODUCTS.map((p) => {
                    const on = !!lines.find((l) => l.product === p);
                    return (
                      <button key={p} type="button" onClick={() => toggleProduct(p)} data-testid={`contractor-product-${p.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-emerald-400"}`}>
                        {on ? <Check className="inline h-3 w-3 mr-1" /> : <Plus className="inline h-3 w-3 mr-1" />}{p}
                      </button>
                    );
                  })}
                </div>
              </div>
              {lines.length > 0 && (
                <div className="space-y-2" data-testid="contractor-lines">
                  {lines.map((l) => (
                    <div key={l.product} className="flex items-center gap-2">
                      <span className="flex-1 text-sm text-slate-700">{l.product}</span>
                      <div className="relative w-36">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                        <input type="number" min="0" value={l.amount_inr} onChange={(e) => setAmount(l.product, e.target.value)} data-testid={`contractor-amount-${l.product.replace(/\s+/g, "-").toLowerCase()}`} className={`${INPUT} pl-6 py-2`} />
                      </div>
                      <button onClick={() => toggleProduct(l.product)} className="text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 text-sm font-semibold text-slate-900">
                    <span>Total</span><span className="font-mono" data-testid="contractor-total">{inr(total)}</span>
                  </div>
                </div>
              )}
              <button onClick={submit} disabled={submitting} data-testid="submit-contractor-invoice" className={`${BTN} px-5 py-2.5 disabled:opacity-60`}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
              </button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">My raised invoices</h2>
            <div className="mt-4 space-y-2 max-h-[560px] overflow-auto">
              {mine.length === 0 && <p className="text-sm text-slate-400">None raised for this client yet.</p>}
              {mine.map((h) => (
                <div key={h.id} data-testid="contractor-mine-row" className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 truncate">{h.property_name}</p>
                    <p className="text-xs text-slate-400">{fmt(h.created_at)} · {inr(h.total_inr)}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border shrink-0 ${h.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{h.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
